import {
  MarketDataUnresolvedError,
  parseMarketDataUnresolvedDetail,
} from "./marketDataResolution";
import { isSessionAuthFailure } from "../lib/sessionAuthFailure";
import { parseApiError, parseApiErrorBody, type ParsedApiError } from "./parseApiError";

export type ValidationFieldError = {
  loc: (string | number)[];
  msg: string;
  type?: string;
};

export class ApiRequestError extends Error {
  slug: string;
  status: number;
  action?: string;
  details?: Record<string, unknown>;
  retryAfterSeconds?: number;
  /** @deprecated Use `message` — kept for legacy catch blocks */
  detail?: string;

  constructor(message: string, status: number, parsed: ParsedApiError) {
    super(message);
    this.name = "ApiRequestError";
    this.slug = parsed.slug;
    this.status = status;
    this.action = parsed.action;
    this.details = parsed.details;
    this.retryAfterSeconds = parsed.retryAfterSeconds;
    this.detail = message;
  }
}

export class ValidationApiError extends ApiRequestError {
  fields: ValidationFieldError[];

  constructor(message: string, status: number, parsed: ParsedApiError, fields: ValidationFieldError[]) {
    super(message, status, parsed);
    this.name = "ValidationApiError";
    this.fields = fields;
  }
}

function parseValidationFields(details?: Record<string, unknown>): ValidationFieldError[] | null {
  const raw = details?.fields;
  if (!Array.isArray(raw)) return null;
  const fields: ValidationFieldError[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const loc = Array.isArray(row.loc) ? row.loc : [];
    const msg = typeof row.msg === "string" ? row.msg : "";
    if (!msg) continue;
    fields.push({
      loc,
      msg,
      type: typeof row.type === "string" ? row.type : undefined,
    });
  }
  return fields.length > 0 ? fields : null;
}

export function throwFromParsedApiError(
  status: number,
  parsed: ParsedApiError,
  fallbackMessage?: string
): never {
  const message = parsed.message?.trim() || fallbackMessage || "Something went wrong.";

  if (status === 422) {
    const unresolved = parseMarketDataUnresolvedDetail(parsed.rawBody ?? parsed);
    if (unresolved) {
      throw new MarketDataUnresolvedError(unresolved, 422);
    }
    const fields = parseValidationFields(parsed.details);
    if (parsed.slug === "validation.unprocessable" && fields) {
      throw new ValidationApiError(message, status, parsed, fields);
    }
  }

  throw new ApiRequestError(message, status, parsed);
}

export async function throwIfNotOk(response: Response, fallbackMessage: string): Promise<void> {
  if (response.ok) return;
  if (await isSessionAuthFailure(response)) {
    throw new ApiRequestError("Session expired. Please log in again.", 401, {
      slug: "auth.token_expired",
      message: "Session expired. Please log in again.",
    });
  }
  const parsed = await parseApiError(response);
  throwFromParsedApiError(response.status, parsed, fallbackMessage);
}

export async function parseJsonOrThrow<T>(
  response: Response,
  fallbackMessage: string,
  options?: { allow204?: boolean }
): Promise<T> {
  if (response.ok) {
    if (response.status === 204) {
      return null as T;
    }

    const text = await response.text();
    if (!text.trim()) {
      if (options?.allow204) {
        return null as T;
      }
      throw new ApiRequestError(fallbackMessage, response.status, {
        slug: "http_error",
        message: fallbackMessage,
      });
    }

    try {
      return JSON.parse(text) as T;
    } catch {
      if (options?.allow204) {
        return null as T;
      }
      throw new ApiRequestError(fallbackMessage, response.status, {
        slug: "http_error",
        message: fallbackMessage,
      });
    }
  }

  if (await isSessionAuthFailure(response)) {
    throw new ApiRequestError("Session expired. Please log in again.", 401, {
      slug: "auth.token_expired",
      message: "Session expired. Please log in again.",
    });
  }

  const parsed = await parseApiError(response);
  throwFromParsedApiError(response.status, parsed, fallbackMessage);
}

/** When the body is already parsed (e.g. billing status). */
export function throwFromErrorBody(
  status: number,
  body: unknown,
  fallbackMessage: string
): never {
  const parsed = parseApiErrorBody(
    body && typeof body === "object" ? (body as Record<string, unknown>) : {},
    fallbackMessage
  );
  throwFromParsedApiError(status, parsed, fallbackMessage);
}

export function isApiRequestError(err: unknown): err is ApiRequestError {
  return err instanceof ApiRequestError;
}

export function getApiErrorMessage(err: unknown, fallback = "Something went wrong."): string {
  if (err instanceof Error && err.message.trim()) return err.message.trim();
  return fallback;
}
